import React from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "../components/useDocumentMeta";
import { caseStudies } from "../content/cases";
import { corePrinciples } from "../content/offering";
import { eventGallery, gothenburgGallery } from "../content/events";
import ImageMarquee from "../components/ImageMarquee";
import CtaBand from "../components/CtaBand";
import CountUp from "../components/CountUp";
import heroPhoto from "../assets/images/events/IMG_2922.jpg";

// Interleave the two event galleries for variety in the scrolling band.
const behindScenesPhotos = gothenburgGallery.flatMap((photo, index) =>
  eventGallery[index] ? [photo, eventGallery[index]] : [photo],
);

const heroStats = [
  {
    eyebrow: "Experience",
    value: "25",
    label:
      "consultants with building Developer Platforms on Backstage and Red Hat Developer Hub",
  },
  {
    eyebrow: "Adoption",
    value: "68%",
    label: "growth in platform self-service adoption at a customer platform",
  },
  {
    eyebrow: "Impact",
    value: "31%",
    label: "less redundant and duplicated work across customer teams",
  },
];

function Home() {
  useDocumentMeta(
    "Developer Platforms, delivered",
    "BoldBrave and Logikfabriken build Internal Developer Platforms on Red Hat Developer Hub and Backstage — for real, in production, at scale.",
  );

  const featured = caseStudies.slice(0, 3);

  return (
    <>
      <section className="hero">
        <div className="hero-visual" aria-hidden="true">
          <img src={heroPhoto} alt="" loading="eager" />
        </div>
        <div className="hero-inner">
          <div className="hero-copy">
            <h1 className="hero-title">Platforms developers actually use</h1>
            <p className="hero-lead">
              Too many platforms are built in siloes with an infrastructure
              mindset, leaving developers disconnected and adoption stalled.
              We're developers and DevEx leads building IDPs that run in
              production at scale, with real incentives for developers to use
              them.
            </p>
            <div className="hero-cta-row">
              <Link className="cta-button" to="/contact">
                Talk to us
              </Link>
              <Link className="cta-button cta-button-outline" to="/assessment">
                Find your starting point
              </Link>
              <Link className="link-button-arrow" to="/work">
                See the track record →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="strip-stats">
        <div className="container stat-block stat-block-3">
          {heroStats.map((stat) => (
            <div className="stat" key={stat.label}>
              <span className="stat-eyebrow">{stat.eyebrow}</span>
              <span className="stat-value">
                <CountUp value={stat.value} />
              </span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-head">
            <p className="kicker">Track record</p>
            <h2 className="section-title">Built in the real world</h2>
            <p className="section-intro">
              Not management consultants delivering slide decks — developers who
              have done this for years, at some of Europe's most demanding tech
              organisations.
            </p>
          </div>

          <div className="card-grid card-grid-3">
            {featured.map((study) => (
              <Link
                key={study.slug}
                to={`/work#${study.slug}`}
                className="teaser-card"
              >
                <div className="teaser-media">
                  <img src={study.image} alt={study.client} loading="lazy" />
                  <span className={`status-pill status-${study.status}`}>
                    {study.status === "ongoing" ? "Ongoing" : "Delivered"}
                  </span>
                </div>
                <div className="teaser-content">
                  <span className="teaser-label">
                    {study.tags.slice(0, 2).join(" · ")}
                  </span>
                  <h3>{study.client}</h3>
                  <p>{study.summary}</p>
                  <span className="teaser-link">Read the story →</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="section-foot">
            <Link className="link-button-arrow" to="/work">
              All case studies →
            </Link>
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <div className="section-head">
            <p className="kicker">How we work</p>
            <h2 className="section-title">Principles that hold every time</h2>
          </div>
          <div className="card-grid card-grid-3">
            {corePrinciples.slice(0, 3).map((p) => (
              <div className="principle-card" key={p.title}>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
          <div className="section-foot">
            <Link className="link-button-arrow" to="/offering">
              Explore our offering →
            </Link>
          </div>
        </div>
      </section>

      <section className="section behind-scenes">
        <div className="container">
          <div className="section-head">
            <p className="kicker">Behind the scenes</p>
            <h2 className="section-title">From our events</h2>
            <p className="section-intro">
              Keynotes, live demos and a lot of good conversations — moments
              from our Platform Engineering events around the Nordics.
            </p>
          </div>
        </div>
        <ImageMarquee photos={behindScenesPhotos} />
      </section>

      <CtaBand
        title="Not sure where to start?"
        text="Answer four questions and we'll point you to where the biggest gains usually hide, and how we've solved it before."
        to="/assessment"
        label="Start the assessment"
      />
    </>
  );
}

export default Home;
